import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Mail, Lock, User, Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  supabase,
  signUpWithEmail,
  signInWithEmail,
  resetPassword,
  isSupabaseConfigured,
} from '../lib/supabase';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get full URL for uploaded assets
const getFullUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return `${API}${url.startsWith('/api') ? '' : '/api'}${url}`;
  return `${API}/api/${url}`;
};

// Brand Logo component that uses uploaded logo
const BrandLogo = ({ logoUrl, size = 'default' }) => {
  const logoSize = size === 'large' ? 'h-12' : 'h-10';
  const textSize = size === 'large' ? 'text-3xl' : 'text-2xl';
  return (
    <div className="flex items-center gap-3">
      <img src={logoUrl} alt="MedStudyPro" className={`${logoSize} object-contain`} />
      <span className={`${textSize} font-bold text-[#1E3A5C]`}>MedStudyPro</span>
    </div>
  );
};

// Default MedStudyPro Logo Component - Updated geometric M design
const DefaultLogo = ({ size = 'default' }) => {
  const logoSize = size === 'large' ? 'h-12 w-12' : 'h-10 w-10';
  const textSize = size === 'large' ? 'text-3xl' : 'text-2xl';
  
  return (
    <div className="flex items-center gap-3">
      <svg viewBox="0 0 40 40" className={logoSize} fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Left M leg */}
        <path d="M4 32V10L12 18L20 10" stroke="#7DD3FC" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
        {/* Right M leg */}
        <path d="M20 10L28 18L36 10V32" stroke="#7DD3FC" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
        {/* Center cube - top face */}
        <path d="M20 16L24 18V22L20 24L16 22V18L20 16Z" fill="#334155"/>
        {/* Center cube - visible faces */}
        <path d="M20 16L24 18L20 20L16 18L20 16Z" fill="#475569"/>
        <path d="M20 20L24 18V22L20 24V20Z" fill="#7DD3FC" fillOpacity="0.6"/>
        <path d="M20 20L16 18V22L20 24V20Z" fill="#7DD3FC" fillOpacity="0.4"/>
      </svg>
      <span className={`${textSize} font-bold text-[#1E3A5C]`}>MedStudyPro</span>
    </div>
  );
};

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/dashboard';
  const hasChecked = useRef(false);
  
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'forgot' | 'reset-sent' | 'check-email'
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
  });
  
  const [errors, setErrors] = useState({});

  // Fetch logo settings
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await fetch(`${API}/api/logo-settings`);
        if (res.ok) {
          const data = await res.json();
          setLogoUrl(data.logo_url || data.logo_light_url);
        }
      } catch (err) {
        console.error('Failed to fetch logo:', err);
      }
    };
    fetchLogo();
  }, []);

  useEffect(() => {
    if (hasChecked.current) return;
    hasChecked.current = true;
    
    // Check if Supabase is configured
    if (!isSupabaseConfigured()) {
      console.error('Supabase not configured');
      setLoading(false);
      return;
    }
    
    // Check if user is already logged in
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          // User has Supabase session - check if they need to subscribe
          // Sync with backend first
          await syncUserWithBackend(session.user);
          
          // Check subscription status before redirecting
          try {
            const statusRes = await fetch(`${API}/api/subscription/status`, {
              credentials: 'include',
            });
            if (statusRes.ok) {
              const status = await statusRes.json();
              
              // If admin, go to dashboard
              if (status.isAdmin) {
                navigate('/dashboard', { replace: true });
                return;
              }
              
              // If subscribed, go to dashboard
              if (status.subscriptionActive) {
                const savedRedirect = localStorage.getItem('redirectAfterAuth') || '/dashboard';
                localStorage.removeItem('redirectAfterAuth');
                navigate(savedRedirect, { replace: true });
                return;
              }
              
              // Not subscribed - stay on auth page to let them know, or go to pricing
              // Check if they came from pricing with a plan
              const plan = searchParams.get('plan');
              if (plan) {
                // They want to subscribe - proceed to create checkout
                setLoading(false);
                handleCheckoutAfterAuth(session.user, plan);
                return;
              }
              
              // No plan selected - redirect to pricing
              navigate('/pricing', { replace: true });
              return;
            }
          } catch (error) {
            console.error('Status check error:', error);
          }
        }
      } catch (error) {
        console.error('Session check error:', error);
      }
      setLoading(false);
    };
    
    checkSession();
  }, [navigate, redirectTo, searchParams]);
  
  const handleCheckoutAfterAuth = async (user, planId) => {
    try {
      const res = await fetch(`${API}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          planId: planId,
          returnUrl: window.location.origin + '/pricing'
        })
      });
      
      const contentType = res.headers.get('content-type') || '';
      const raw = await res.text();
      let data = null;
      
      if (raw && contentType.includes('application/json')) {
        try { data = JSON.parse(raw); } catch (e) {}
      }
      
      if (res.ok && data?.url) {
        localStorage.removeItem('selectedPlan');
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout. Please try again.');
        navigate('/pricing', { replace: true });
      }
    } catch (error) {
      console.error('Checkout error:', error);
      navigate('/pricing', { replace: true });
    }
  };

  const syncUserWithBackend = async (user) => {
    try {
      await fetch(`${API}/api/auth/supabase-sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          supabase_user_id: user.id,
          email: user.email,
          full_name: user.user_metadata?.full_name || user.email?.split('@')[0],
        }),
      });
    } catch (error) {
      console.error('Failed to sync user with backend:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (mode !== 'forgot') {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (mode === 'signup' && formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
    }
    
    if (mode === 'signup') {
      if (!formData.fullName.trim()) {
        newErrors.fullName = 'Full name is required';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    if (!isSupabaseConfigured()) {
      toast.error('Authentication service not configured. Please contact support.');
      return;
    }
    
    setSubmitting(true);
    
    try {
      if (mode === 'signin') {
        const { data, error } = await signInWithEmail(formData.email, formData.password);
        if (error) throw error;
        
        if (data.user) {
          toast.success('Signed in successfully!');
          await syncUserWithBackend(data.user);
          
          const savedRedirect = localStorage.getItem('redirectAfterAuth') || '/dashboard';
          localStorage.removeItem('redirectAfterAuth');
          navigate(savedRedirect, { replace: true });
        }
      } else if (mode === 'signup') {
        const { data, error } = await signUpWithEmail(
          formData.email,
          formData.password,
          formData.fullName
        );
        if (error) throw error;
        
        // Check if we got a session (email confirmation disabled) or just a user (confirmation required)
        if (data.session) {
          // Email confirmation is disabled - user is logged in immediately
          toast.success('Account created successfully!');
          const syncResult = await syncUserWithBackend(data.user);
          
          // Check if user is admin based on email (frontend check for immediate redirect)
          const adminEmails = ['medstudypro1@gmail.com', 'medstudypro@gmail.com'];
          const isAdmin = adminEmails.includes(data.user.email?.toLowerCase());
          
          if (isAdmin) {
            navigate('/dashboard', { replace: true });
          } else {
            // New users go to pricing page
            navigate('/pricing', { replace: true });
          }
        } else if (data.user && !data.user.confirmed_at) {
          // Email confirmation required
          setMode('check-email');
        } else if (data.user) {
          toast.success('Account created successfully!');
          await syncUserWithBackend(data.user);
          navigate('/pricing', { replace: true });
        }
      } else if (mode === 'forgot') {
        const { error } = await resetPassword(formData.email);
        if (error) throw error;
        
        setMode('reset-sent');
      }
    } catch (error) {
      console.error('Auth error:', error);
      // Provide more user-friendly error messages
      let errorMessage = error.message || 'Authentication failed';
      if (error.message?.includes('Invalid login credentials')) {
        errorMessage = 'Invalid email or password. Please try again.';
      } else if (error.message?.includes('Email not confirmed')) {
        errorMessage = 'Please check your email and confirm your account first.';
      } else if (error.message?.includes('email_address_invalid')) {
        errorMessage = 'Please use a valid email address.';
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#192A3F] to-[#0F1E2D] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF9E00] mx-auto mb-4" />
          <p className="text-white/70">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Check email confirmation screen
  if (mode === 'check-email') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#192A3F] to-[#0F1E2D] flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl p-8 shadow-xl text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#1E3A5C] mb-2">Check Your Email</h1>
            <p className="text-slate-600 mb-6">
              We&apos;ve sent a confirmation link to <strong>{formData.email}</strong>. 
              Please click the link to verify your account.
            </p>
            <Button
              onClick={() => setMode('signin')}
              variant="outline"
              className="w-full h-12 rounded-full"
            >
              Back to Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Password reset sent screen
  if (mode === 'reset-sent') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#192A3F] to-[#0F1E2D] flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl p-8 shadow-xl text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#1E3A5C] mb-2">Reset Link Sent</h1>
            <p className="text-slate-600 mb-6">
              If an account exists for <strong>{formData.email}</strong>, you&apos;ll receive 
              a password reset link shortly.
            </p>
            <Button
              onClick={() => setMode('signin')}
              variant="outline"
              className="w-full h-12 rounded-full"
            >
              Back to Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#192A3F] to-[#0F1E2D] flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl p-8 shadow-xl">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            {logoUrl ? (
              <BrandLogo logoUrl={getFullUrl(logoUrl)} size="large" />
            ) : (
              <DefaultLogo size="large" />
            )}
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-[#1E3A5C] text-center mb-2">
            {mode === 'signin' && 'Welcome Back'}
            {mode === 'signup' && 'Create Your Account'}
            {mode === 'forgot' && 'Reset Your Password'}
          </h1>
          <p className="text-slate-600 text-center mb-6">
            {mode === 'signin' && 'Sign in to continue your medical learning journey.'}
            {mode === 'signup' && 'Join thousands of medical professionals.'}
            {mode === 'forgot' && 'Enter your email to receive a reset link.'}
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full Name (signup only) */}
            {mode === 'signup' && (
              <div>
                <Label htmlFor="fullName" className="text-[#1E3A5C]">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData(p => ({ ...p, fullName: e.target.value }))}
                    placeholder="John Doe"
                    className={`pl-10 h-12 bg-[#F8FAFD] border-[#E8EEF3] rounded-xl ${errors.fullName ? 'border-red-500' : ''}`}
                    data-testid="auth-fullname-input"
                  />
                </div>
                {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
              </div>
            )}

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-[#1E3A5C]">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                  placeholder="you@example.com"
                  className={`pl-10 h-12 bg-[#F8FAFD] border-[#E8EEF3] rounded-xl ${errors.email ? 'border-red-500' : ''}`}
                  data-testid="auth-email-input"
                />
              </div>
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>

            {/* Password */}
            {mode !== 'forgot' && (
              <div>
                <Label htmlFor="password" className="text-[#1E3A5C]">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))}
                    placeholder="••••••••"
                    className={`pl-10 pr-10 h-12 bg-[#F8FAFD] border-[#E8EEF3] rounded-xl ${errors.password ? 'border-red-500' : ''}`}
                    data-testid="auth-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
              </div>
            )}

            {/* Confirm Password (signup only) */}
            {mode === 'signup' && (
              <div>
                <Label htmlFor="confirmPassword" className="text-[#1E3A5C]">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData(p => ({ ...p, confirmPassword: e.target.value }))}
                    placeholder="••••••••"
                    className={`pl-10 pr-10 h-12 bg-[#F8FAFD] border-[#E8EEF3] rounded-xl ${errors.confirmPassword ? 'border-red-500' : ''}`}
                    data-testid="auth-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
              </div>
            )}

            {/* Forgot Password Link */}
            {mode === 'signin' && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setMode('forgot')}
                  className="text-sm text-[#FF9E00] hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-[#FF9E00] hover:bg-[#E88D00] text-white rounded-full font-semibold"
              data-testid="auth-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {mode === 'signin' && 'Signing In...'}
                  {mode === 'signup' && 'Creating Account...'}
                  {mode === 'forgot' && 'Sending Reset Link...'}
                </>
              ) : (
                <>
                  {mode === 'signin' && 'Sign In'}
                  {mode === 'signup' && 'Create Account'}
                  {mode === 'forgot' && 'Send Reset Link'}
                </>
              )}
            </Button>
          </form>

          {/* Back to signin from forgot */}
          {mode === 'forgot' && (
            <button
              onClick={() => setMode('signin')}
              className="w-full mt-4 flex items-center justify-center gap-2 text-slate-600 hover:text-[#1E3A5C]"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Sign In
            </button>
          )}

          {/* Toggle between signin/signup */}
          {mode !== 'forgot' && (
            <p className="text-center text-sm text-slate-500 mt-6">
              {mode === 'signin' ? (
                <>
                  Don&apos;t have an account?{' '}
                  <button
                    onClick={() => setMode('signup')}
                    className="text-[#FF9E00] hover:underline font-medium"
                  >
                    Sign up
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button
                    onClick={() => setMode('signin')}
                    className="text-[#FF9E00] hover:underline font-medium"
                  >
                    Sign in
                  </button>
                </>
              )}
            </p>
          )}
        </div>

        {/* Back to home */}
        <p className="text-center text-white/50 text-xs mt-6">
          <button onClick={() => navigate('/')} className="hover:text-white/70">
            ← Back to home
          </button>
        </p>
      </div>
    </div>
  );
}
